import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './context/AuthContext';
import { RequireAuth } from './components/RequireAuth';
import ScrollToTop from './components/ScrollToTop';
import { useAuth } from './hooks/useAuth';
import PushNotificationManager from './components/PushNotificationManager';
import { isDemoMode } from './utils/demoMode';
import './index.css';

const StudioDashboardLayout = lazy(() => import('./components/DashboardLayout'));
const ArtistDashboardLayout = lazy(
  () => import('./components/ArtistDashboardLayout')
);
const Home = lazy(() => import('./pages/Home'));
const DemoExperience = lazy(() => import('./pages/DemoExperience'));
const SignUp = lazy(() => import('./components/SignUpPage'));
const RegisterScreen = lazy(() => import('./pages/RegisterScreen'));
const Login = lazy(() => import('./pages/Login'));
const CustomerOnboardingFlow = lazy(
  () => import('./components/CustomerOnboardingFlow')
);
const BookAgainOnboardingFlow = lazy(
  () => import('./components/BookingAgainOnboardingFlow')
);
const SMSConsentPage = lazy(() => import('./pages/SMSConsentPage'));
const CreateStudio = lazy(() => import('./pages/CreateStudio'));
const StudioBookingCalendarWrapper = lazy(
  () => import('./components/StudioBookingCalendarWrapper')
);
const StudioFormEditor = lazy(() => import('./components/StudioFormEditor'));
const StudioProfileScreen = lazy(() => import('./pages/StudioProfileScreen'));
const AddArtistPage = lazy(() => import('./components/AddArtistPage'));
const ArtistListScreen = lazy(() => import('./pages/ArtistListScreen'));
const CustomerListScreen = lazy(() => import('./pages/CustomerListScreen'));
const CustomerProfilePage = lazy(() => import('./pages/CustomerProfilePage'));
const StudioAnalytics = lazy(() => import('./pages/StudioAnalyticsScreen'));
const StudioNotificationsPanel = lazy(
  () => import('./components/StudioNotificationsPanel')
);
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const ArtistProfilePage = lazy(() => import('./pages/ArtistProfilePage'));
const ArtistCustomersPage = lazy(() => import('./pages/ArtistCustomersPage'));
const ArtistAvailability = lazy(() => import('./pages/ArtistAvailability'));
const ArtistFlashPage = lazy(() => import('./pages/ArtistFlashPage'));
const SubscribePage = lazy(() => import('./components/SubscribePage'));
const ArtistProfileForm = lazy(() => import('./components/ArtistProfileForm'));
const SubscriptionSuccessRedirect = lazy(
  () => import('./pages/SubscriptionSuccessRedirect')
);
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const CreateMasterStudioTool = lazy(() =>
  import('./components/CreateMasterStudioTool').then((m) => ({
    default: m.CreateMasterStudioTool,
  }))
);
const AftercarePage = lazy(() => import('./pages/AfterCarePage'));
const ExpensesScreenWrapper = lazy(() => import('./pages/ExpensesScreenWrapper'));
const ArtistNotificationsPanel = lazy(
  () => import('./components/ArtistNotificationsPanel')
);
const ArtistProfileScreen = lazy(() => import('./pages/ArtistProfileScreen'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword'));
const BillingRequiredPage = lazy(() => import('./pages/BillingRequiredPage'));
const ArtistBookingCalendar = lazy(() => import('./pages/ArtistBookingCalendar'));
const PublicArtistBooking = lazy(() => import('./pages/PublicArtistBooking'));
const StudioStorefrontMock = lazy(() => import('./pages/StudioStorefrontMock'));
const LegacyOnboardingRedirect = lazy(
  () => import('./pages/LegacyOnboardingRedirect')
);
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'));

const theme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: '#000' },
    primary: { main: '#d3b33f' },
  },
  typography: {
    fontFamily: 'Manrope, sans-serif',
    h1: { fontFamily: 'Raleway, sans-serif', fontWeight: 700 },
    h2: { fontFamily: 'Raleway, sans-serif', fontWeight: 700 },
    h3: { fontFamily: 'Raleway, sans-serif', fontWeight: 600 },
    h4: { fontFamily: 'Raleway, sans-serif', fontWeight: 600 },
    h5: { fontFamily: 'Raleway, sans-serif', fontWeight: 600, fontSize: '2rem' },
    h6: { fontFamily: 'Raleway, sans-serif', fontWeight: 500 },
  },
});


const LandingRedirect = () => {
  const { user, userRole, studioId, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white font-manrope">
        Loading…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!userRole) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white font-manrope">
        Loading profile…
      </div>
    );
  }

  if (userRole === 'studio' && studioId) {
    return <Navigate to={`/studio/${studioId}/dashboard`} replace />;
  }

  if (userRole === 'studio' && !studioId) {
    return <Navigate to="/admin/create-studio" replace />;
  }

  return <Navigate to="/artist/bookings" replace />;
};

const AppContent = () => {
  const location = useLocation();

  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white font-manrope">
          Loading…
        </div>
      }
    >
      <ScrollToTop />
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<LandingRedirect />} />
        <Route path="/home" element={<Home />} />
        <Route path="/demo" element={<DemoExperience />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/register" element={<RegisterScreen />} />
        <Route path="/login" element={<Login />} />
        <Route path="/onboarding/:studioId/:bookingId/:customerId" element={<CustomerOnboardingFlow />} />
        <Route path="/customer-onboarding/:studioId/:bookingId/:customerId" element={<CustomerOnboardingFlow />} />
        <Route path="/onboarding-independent/:artistId/:bookingId/:customerId" element={<LegacyOnboardingRedirect />} />
        <Route path="/onboarding-book-again/:studioId/:bookingId/:customerId" element={<BookAgainOnboardingFlow />} />
        <Route path="/sms-consent" element={<SMSConsentPage />} />
        <Route path="/book/:artistId" element={<PublicArtistBooking />} />
        <Route path="/book/:studioId/:artistId" element={<PublicArtistBooking />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />


        {/* NEW: Aftercare Page - Top-level route */}
        <Route path="/aftercare/:studioId" element={<AftercarePage />} />

        {/* ✅ The correct subscription flow routes */}
        <Route path="/subscribe" element={<SubscribePage />} />
        <Route path="/billing-required" element={<BillingRequiredPage />} />
        <Route path="/subscription-success" element={<SubscriptionSuccessRedirect />} />
        <Route path="/finish-profile" element={<ArtistProfileForm />} />

        <Route
          path="/admin/create-studio"
          element={
            <RequireAuth>
              <CreateStudio />
            </RequireAuth>
          }
        />
        <Route path="/admin/create-master-studio" element={<CreateMasterStudioTool />} />

        {/* Studio Dashboard Routes */}
        <Route path="/studio/:studioId" element={<RequireAuth><StudioDashboardLayout /></RequireAuth>}>
          <Route index element={<StudioBookingCalendarWrapper />} />
          <Route path="dashboard" element={<StudioBookingCalendarWrapper />} />
          <Route path="calendar" element={<StudioBookingCalendarWrapper />} />
          <Route path="forms-editor" element={<StudioFormEditor />} />
          <Route path="profile" element={<StudioProfileScreen />} />
          <Route path="add-artist" element={<AddArtistPage />} />
          <Route path="artists" element={<ArtistListScreen />} />
          <Route path="customers" element={<CustomerListScreen />} />
          <Route path="customers/:customerId" element={<CustomerProfilePage />} />
          <Route path="analytics" element={<StudioAnalytics />} />
          <Route path="storefront" element={<StudioStorefrontMock />} />
          <Route path="expenses" element={<ExpensesScreenWrapper />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="notifications" element={<StudioNotificationsPanel />} />
          <Route path="account-settings" element={<StudioProfileScreen />} />
          {/* ⬇️ UPDATED: Messages (with deep link) */}
          <Route path="messages" element={<MessagesPage />} />
          <Route path="messages/:conversationId" element={<MessagesPage />} />
          <Route path="artists/:artistId" element={<ArtistProfilePage />} />
        </Route>

        <Route path="/admin" element={<RequireAuth><AdminDashboard /></RequireAuth>} />

        {/* Artist Dashboard Routes */}
        <Route path="/artist" element={<RequireAuth><ArtistDashboardLayout /></RequireAuth>}>
          {/* Default = Bookings */}
          <Route index element={<Navigate to="bookings" replace />} />

          {/* Calendar */}
          <Route path="bookings" element={<ArtistBookingCalendar />} />

          {/* Old "dashboard" path → calendar */}
          <Route path="dashboard" element={<Navigate to="../bookings" replace />} />

          {/* Messages routes */}
          <Route path="messages" element={<MessagesPage />} />
          <Route path="messages/:conversationId" element={<MessagesPage />} />
          <Route path="flash" element={<ArtistFlashPage />} />
          {/* Back-compat alias: old /artist/messaging -> /artist/messages */}
          <Route path="messaging" element={<Navigate to="../messages" replace />} />

          {/* Other sections */}
          <Route path="customers" element={<ArtistCustomersPage />} />
          <Route path="customers/:customerId" element={<CustomerProfilePage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="notifications" element={<ArtistNotificationsPanel />} />
          <Route path="settings" element={<ArtistProfileScreen />} />
          <Route path="availability" element={<ArtistAvailability />} />
          <Route path="forms-editor" element={<StudioFormEditor />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/artist/bookings" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

const App = () => {
  useEffect(() => {
    const applyTheme = (mode: 'light' | 'dark') => {
      if (typeof document === 'undefined') return;
      document.documentElement.setAttribute('data-theme', mode);
    };

    const saved = (typeof window !== 'undefined'
      ? window.localStorage.getItem('dashboardThemeMode')
      : null) as 'light' | 'dark' | null;
    applyTheme(saved === 'light' ? 'light' : 'dark');

    const onThemeChange = (evt: Event) => {
      const mode = (evt as CustomEvent<'light' | 'dark'>).detail;
      applyTheme(mode === 'light' ? 'light' : 'dark');
    };

    window.addEventListener('dashboard-theme-change', onThemeChange as EventListener);
    return () => {
      window.removeEventListener('dashboard-theme-change', onThemeChange as EventListener);
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        {!isDemoMode && <PushNotificationManager />}
        <AppContent/>
        <ToastContainer position="top-center" autoClose={4000} />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
